import { Draft, produce } from 'immer';
import { DesignerParams } from '@/types';
import { assertDefined } from '@/utils/assertDefined';
import { getColorsForDigitalPrintTypes } from '@/redux/core/utils/getColorsForDigitalPrintTypes';
import { getConfiguredFeatures, getFeatures } from './features';
import { CoreAction, CoreActionTypes, CoreState } from './types';

const INITIAL_STATE = {
  deliveryEta: {},
  features: getFeatures(),
} as CoreState;

export const getInitialCoreState = (params: DesignerParams) =>
  produce<CoreState>(INITIAL_STATE, (draft) => {
    draft.features = getConfiguredFeatures(params.features);
    return draft;
  });

export const coreReducer = produce((draft: Draft<CoreState>, action: CoreAction) => {
  switch (action.type) {
    case CoreActionTypes.FEATURES_SET:
      draft.features = Object.assign(draft.features, action.features);
      return draft;
    case CoreActionTypes.CURRENCY_FETCH:
      draft.currency = action.currency;
      return draft;
    case CoreActionTypes.CONTEXT_FETCH:
      draft.context = action.payload;
      return draft;
    case CoreActionTypes.FONTS_FETCH:
      draft.fonts = action.payload;
      return draft;
    case CoreActionTypes.SPECIAL_FONTS_FETCH:
      draft.specialFonts = action.payload.map((f) => ({
        ...f,
        icon: f.icon.replace('http', 'https'),
      }));
      return draft;
    case CoreActionTypes.SET_FONT_LOADED: {
      const fontFamily = assertDefined(
        draft.fonts.find((family) => family.fonts.some((f) => f.identifier === action.identifier))
      );
      const font = assertDefined(fontFamily.fonts.find((f) => f.identifier === action.identifier));
      font.supportedGlyphs = action.supportedGlyphs;
      return draft;
    }
    case CoreActionTypes.ENABLE_MEMENTO_CHECK:
      draft.mementoCheckEnabled = true;
      return draft;
    case CoreActionTypes.CURRENCY_EXCHANGE_RATES_FETCH:
      draft.exchangeRates = action.payload;
      return draft;
    case CoreActionTypes.SET_USER:
      draft.user = action.payload;
      return draft;
    case CoreActionTypes.DELIVERY_ETA_FETCH:
      draft.deliveryEta[action.id] = action.payload;
      return draft;
    case CoreActionTypes.PRINT_TYPE_FETCH:
      const printTypes = action.printTypes;
      printTypes.sort((a, b) => a.weight - b.weight);
      draft.printTypes = new Map(printTypes.map((printType) => [printType.id, printType]));
      draft.digitalColors = getColorsForDigitalPrintTypes(draft.printTypes);
      return draft;
    default:
      return draft;
  }
}, INITIAL_STATE);
